import React, { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import "pdfjs-dist/web/pdf_viewer.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import "./TextLayer.css";
import pdfWorker from "pdfjs-dist/build/pdf.worker.entry";

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker;

export const PdfViewer = ({ pdfBase64, zoom, setZoom }) => {
    const containerRef = useRef(null);
    const scrollContainerRef = useRef(null); // novo ref
    const scrollRef = useRef(0); // Ref para armazenar a posição de rolagem vertical
    const [pdf, setPdf] = useState(null);
    const [showMessage, setShowMessage] = useState(false);

    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);

    // Carregar o PDF
    useEffect(() => {
        if (!pdfBase64) return;

        const loadPdf = async () => {
            const raw = atob(pdfBase64.split(",")[1]);
            const uint8Array = Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
            const blob = new Blob([uint8Array], { type: "application/pdf" });
            const blobUrl = URL.createObjectURL(blob);

            const loadingTask = pdfjsLib.getDocument({ data: uint8Array });
            const pdfDoc = await loadingTask.promise;

            let hasSignature = false;
            for (let i = 1; i <= pdfDoc.numPages; i++) {
                const page = await pdfDoc.getPage(i);
                const annotations = await page.getAnnotations();
                if (annotations.some((a) => a.fieldType === "Sig")) {
                    hasSignature = true;
                    break;
                }
            }

            if (hasSignature) {
                window.open(blobUrl, "_blank");
                setShowMessage(true);
            } else {
                setPdf(pdfDoc);
            }
        };

        loadPdf();
    }, [pdfBase64]);

    useEffect(() => {
        if (!pdf || !containerRef.current) return;
    
        const renderPages = async () => {
            const container = containerRef.current;
            container.innerHTML = ""; // Limpar conteúdo antes de renderizar novamente
    
            const innerWrapper = document.createElement("div");
            innerWrapper.style.display = "flex";
            innerWrapper.style.flexDirection = "column";
            innerWrapper.style.alignItems = "center";
            innerWrapper.style.width = "fit-content";
            container.appendChild(innerWrapper);
    
            const pagePromises = [];  // Armazenar promessas das renderizações de páginas
    
            for (let i = 1; i <= pdf.numPages; i++) {
                const page = await pdf.getPage(i);
                const viewport = page.getViewport({ scale: zoom });
    
                const pageWrapper = document.createElement("div");
                pageWrapper.style.position = "relative";
                pageWrapper.style.marginBottom = "20px";
                pageWrapper.style.width = `${viewport.width}px`;
                pageWrapper.style.height = `${viewport.height}px`;
    
                const canvas = document.createElement("canvas");
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                const context = canvas.getContext("2d");
    
                // Promise da renderização da página
                const renderPromise = page.render({
                    canvasContext: context,
                    viewport,
                    renderInteractiveForms: true,
                }).promise;
    
                pagePromises.push(renderPromise); // Adiciona a promessa à lista
    
                pageWrapper.appendChild(canvas);
    
                const textLayerDiv = document.createElement("div");
                textLayerDiv.className = "textLayer";
                textLayerDiv.style.width = `${viewport.width}px`;
                textLayerDiv.style.height = `${viewport.height}px`;
                textLayerDiv.style.position = "absolute";
                textLayerDiv.style.top = "0";
                textLayerDiv.style.left = "0";
                textLayerDiv.style.zIndex = "2";
    
                const textContent = await page.getTextContent();
                await pdfjsLib.renderTextLayer({
                    textContent,
                    container: textLayerDiv,
                    viewport,
                    textDivs: [],
                });
    
                pageWrapper.appendChild(textLayerDiv);
                innerWrapper.appendChild(pageWrapper);
            }
    
            // Aguarde todas as promessas de renderização terminarem
            await Promise.all(pagePromises);
        };
    
        renderPages().then(() => {
            // Depois que a renderização das páginas for concluída, restaurar o scroll
            const el = scrollContainerRef.current;
            if (el) {
                el.scrollTop = scrollRef.current;
            }
        });
    }, [pdf, zoom]);  // Dependência no pdf e zoom
    
    // Função para salvar a posição do scroll vertical durante o scroll
    const handleScroll = (e) => {
        if (e.target.scrollTop == 0) return;
        scrollRef.current = e.target.scrollTop;
    };

    // Função para o arraste lateral
    const handleMouseDown = (e) => {
        if (e.ctrlKey) {
            setIsDragging(true);
            setStartX(e.clientX);
            setScrollLeft(scrollContainerRef.current.scrollLeft);
            scrollContainerRef.current.style.cursor = "grabbing"; // Muda o cursor para "grabbing"
        }
    };

    const handleMouseMove = (e) => {
        if (isDragging) {
            const dx = e.clientX - startX;
            scrollContainerRef.current.scrollLeft = scrollLeft - dx;
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
        scrollContainerRef.current.style.cursor = "default"; // Restaura o cursor para "default"
    };

    // Função para zoom com o scroll do mouse
    const handleWheel = (e) => {
        if (e.ctrlKey) {
            e.preventDefault();
            const newZoom = zoom + (e.deltaY > 0 ? -0.1 : 0.1); // Ajusta o zoom
            setZoom(newZoom);
        }
    };

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener("mousemove", handleMouseMove);
            container.addEventListener("mouseup", handleMouseUp);
            container.addEventListener("mousedown", handleMouseDown);
            container.addEventListener("wheel", handleWheel, { passive: false });
        }

        return () => {
            if (container) {
                container.removeEventListener("mousemove", handleMouseMove);
                container.removeEventListener("mouseup", handleMouseUp);
                container.removeEventListener("mousedown", handleMouseDown);
                container.removeEventListener("wheel", handleWheel);
            }
        };
    }, [zoom, isDragging, startX, scrollLeft]);

    if (showMessage) {
        return (
            <div style={{ textAlign: "center", minHeight: "85vh", padding: "1rem", fontSize: "1.1rem" }}>
                Arquivo carregado em nova aba para permitir visualizar as assinaturas digitais.
            </div>
        );
    }

    return (
        <div
            ref={scrollContainerRef}
            className="custom-scroll"
            style={{
                width: "100vw",
                height: "85vh",
                overflow: "auto",
            }}
            onScroll={handleScroll}
        >
            <div
                ref={containerRef}
                style={{
                    display: "flex",
                    justifyContent: "center",
                    minWidth: "fit-content",
                    padding: "1rem 0",
                }}
            />
        </div>
    );
};
